import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { DictionaryService, CountryDto } from '../../../../services/api/dictionary';
import { RegistrationService } from '../../../../services/api/registration';
import { LanguageService } from '../../../../services/i18n/language';
import { CountrySelectComponent } from '../../../shared/country-select/country-select';
import { LegalConsentComponent } from '../../../legal/legal-consent/legal-consent.component';
import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'app-freight-forwarder-registration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, CountrySelectComponent, LegalConsentComponent],
  templateUrl: './freight-forwarder-registration.html',
  styleUrl: './freight-forwarder-registration.scss'
})
export class FreightForwarderRegistrationComponent implements OnInit, OnDestroy {
  registrationForm: FormGroup;
  countries: CountryDto[] = [];
  loading = false;
  error: string | null = null;
  success = false;

  // Переводы
  titleText = 'Регистрация экспедитора грузов';
  subtitleText = 'Заполните форму для создания аккаунта';
  personalInfoText = 'Личная информация';
  passwordText = 'Пароль';
  companyInfoText = 'Информация о компании';
  emailLabel = 'Email';
  fullNameLabel = 'Полное имя';
  phoneLabel = 'Телефон';
  passwordLabel = 'Пароль';
  confirmPasswordLabel = 'Подтвердите пароль';
  companyNameLabel = 'Название компании';
  countryLabel = 'Страна';
  selectCountryText = 'Выберите страну';
  submitText = 'Зарегистрироваться';
  submittingText = 'Регистрация...';
  hasAccountText = 'Уже есть аккаунт? Войти';
  successText = 'Регистрация успешно завершена! Вы будете перенаправлены на страницу входа...';
  requiredText = '*';
  fullNamePlaceholderText = 'Имя Фамилия';
  passwordPlaceholderText = 'Минимум 6 символов';
  confirmPasswordPlaceholderText = 'Повторите пароль';
  companyNamePlaceholderText = 'ИП/ТОО «Наименование»';
  
  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dictionaryService: DictionaryService,
    private registrationService: RegistrationService,
    private router: Router,
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {
    this.registrationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email, Validators.maxLength(255)]],
      fullName: ['', [Validators.required, Validators.maxLength(255)]],
      phone: ['', [Validators.maxLength(20)]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(255)]],
      confirmPassword: ['', [Validators.required]],
      companyName: ['', [Validators.required, Validators.maxLength(255)]],
      countryId: [null, [Validators.required]],
      acceptTerms: [false, [Validators.requiredTrue]],
      acceptPersonalData: [false, [Validators.requiredTrue]]
    }, { validators: this.passwordMatchValidator });
  }

  ngOnInit(): void {
    this.updateTranslations();
    this.loadDictionaries();

    // Подписываемся на изменения языка
    this.subscriptions.add(
      combineLatest([
        this.languageService.getTranslations(),
        this.languageService.getCurrentLanguage()
      ]).subscribe(() => {
        this.updateTranslations();
        this.loadDictionaries(); // Перезагружаем справочники с новым языком
        this.cdr.detectChanges();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTranslations(): void {
    const titleTranslated = this.languageService.translate('registration.freightForwarder.title');
    this.titleText = titleTranslated && titleTranslated !== 'registration.freightForwarder.title' ? titleTranslated : 'Регистрация экспедитора грузов';
    
    const subtitleTranslated = this.languageService.translate('registration.freightForwarder.subtitle');
    this.subtitleText = subtitleTranslated && subtitleTranslated !== 'registration.freightForwarder.subtitle' ? subtitleTranslated : 'Заполните форму для создания аккаунта';
    
    const personalInfoTranslated = this.languageService.translate('registration.freightForwarder.personalInfo');
    this.personalInfoText = personalInfoTranslated && personalInfoTranslated !== 'registration.freightForwarder.personalInfo' ? personalInfoTranslated : 'Личная информация';
    
    const passwordTranslated = this.languageService.translate('registration.freightForwarder.password');
    this.passwordText = passwordTranslated && passwordTranslated !== 'registration.freightForwarder.password' ? passwordTranslated : 'Пароль';
    
    const companyInfoTranslated = this.languageService.translate('registration.freightForwarder.companyInfo');
    this.companyInfoText = companyInfoTranslated && companyInfoTranslated !== 'registration.freightForwarder.companyInfo' ? companyInfoTranslated : 'Информация о компании';
    
    const emailTranslated = this.languageService.translate('registration.freightForwarder.email');
    this.emailLabel = emailTranslated && emailTranslated !== 'registration.freightForwarder.email' ? emailTranslated : 'Email';
    
    const fullNameTranslated = this.languageService.translate('registration.freightForwarder.fullName');
    this.fullNameLabel = fullNameTranslated && fullNameTranslated !== 'registration.freightForwarder.fullName' ? fullNameTranslated : 'Полное имя';
    
    const phoneTranslated = this.languageService.translate('registration.freightForwarder.phone');
    this.phoneLabel = phoneTranslated && phoneTranslated !== 'registration.freightForwarder.phone' ? phoneTranslated : 'Телефон';
    
    const passwordLabelTranslated = this.languageService.translate('registration.freightForwarder.passwordField');
    this.passwordLabel = passwordLabelTranslated && passwordLabelTranslated !== 'registration.freightForwarder.passwordField' ? passwordLabelTranslated : 'Пароль';
    
    const confirmPasswordTranslated = this.languageService.translate('registration.freightForwarder.confirmPassword');
    this.confirmPasswordLabel = confirmPasswordTranslated && confirmPasswordTranslated !== 'registration.freightForwarder.confirmPassword' ? confirmPasswordTranslated : 'Подтвердите пароль';
    
    const companyNameTranslated = this.languageService.translate('registration.freightForwarder.companyName');
    this.companyNameLabel = companyNameTranslated && companyNameTranslated !== 'registration.freightForwarder.companyName' ? companyNameTranslated : 'Название компании';
    
    const countryTranslated = this.languageService.translate('registration.freightForwarder.country');
    this.countryLabel = countryTranslated && countryTranslated !== 'registration.freightForwarder.country' ? countryTranslated : 'Страна';
    
    const selectCountryTranslated = this.languageService.translate('registration.freightForwarder.selectCountry');
    this.selectCountryText = selectCountryTranslated && selectCountryTranslated !== 'registration.freightForwarder.selectCountry' ? selectCountryTranslated : 'Выберите страну';
    
    const submitTranslated = this.languageService.translate('registration.freightForwarder.submit');
    this.submitText = submitTranslated && submitTranslated !== 'registration.freightForwarder.submit' ? submitTranslated : 'Зарегистрироваться';
    
    const submittingTranslated = this.languageService.translate('registration.freightForwarder.submitting');
    this.submittingText = submittingTranslated && submittingTranslated !== 'registration.freightForwarder.submitting' ? submittingTranslated : 'Регистрация...';
    
    const hasAccountTranslated = this.languageService.translate('registration.freightForwarder.hasAccount');
    this.hasAccountText = hasAccountTranslated && hasAccountTranslated !== 'registration.freightForwarder.hasAccount' ? hasAccountTranslated : 'Уже есть аккаунт? Войти';
    
    const successTranslated = this.languageService.translate('registration.freightForwarder.success');
    this.successText = successTranslated && successTranslated !== 'registration.freightForwarder.success' ? successTranslated : 'Регистрация успешно завершена! Вы будете перенаправлены на страницу входа...';
    
    const requiredTranslated = this.languageService.translate('registration.freightForwarder.required');
    this.requiredText = requiredTranslated && requiredTranslated !== 'registration.freightForwarder.required' ? requiredTranslated : '*';

    const fullNamePlaceholderTranslated = this.languageService.translate('registration.cargoOwner.fullNamePlaceholder');
    this.fullNamePlaceholderText = fullNamePlaceholderTranslated && fullNamePlaceholderTranslated !== 'registration.cargoOwner.fullNamePlaceholder' ? fullNamePlaceholderTranslated : 'Имя Фамилия';

    const passwordPlaceholderTranslated = this.languageService.translate('registration.cargoOwner.passwordPlaceholder');
    this.passwordPlaceholderText = passwordPlaceholderTranslated && passwordPlaceholderTranslated !== 'registration.cargoOwner.passwordPlaceholder' ? passwordPlaceholderTranslated : 'Минимум 6 символов';

    const confirmPasswordPlaceholderTranslated = this.languageService.translate('registration.cargoOwner.confirmPasswordPlaceholder');
    this.confirmPasswordPlaceholderText = confirmPasswordPlaceholderTranslated && confirmPasswordPlaceholderTranslated !== 'registration.cargoOwner.confirmPasswordPlaceholder' ? confirmPasswordPlaceholderTranslated : 'Повторите пароль';

    const companyNamePlaceholderTranslated = this.languageService.translate('registration.cargoOwner.companyNamePlaceholder');
    this.companyNamePlaceholderText = companyNamePlaceholderTranslated && companyNamePlaceholderTranslated !== 'registration.cargoOwner.companyNamePlaceholder' ? companyNamePlaceholderTranslated : 'ИП/ТОО «Наименование»';
  }

  passwordMatchValidator(form: FormGroup) {
    const password = form.get('password');
    const confirmPassword = form.get('confirmPassword');
    
    if (password && confirmPassword && password.value !== confirmPassword.value) {
      confirmPassword.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    }
    
    if (confirmPassword && confirmPassword.hasError('passwordMismatch')) {
      confirmPassword.setErrors(null);
    }
    
    return null;
  }

  loadDictionaries(): void {
    this.dictionaryService.getAllCountries().subscribe({
      next: (countries) => {
        this.countries = countries;
        // Казахстан выбран по умолчанию, пользователь может сменить
        const kazakhstan = countries.find(c => c.code === 'KZ');
        if (kazakhstan && !this.registrationForm.get('countryId')?.value) {
          this.registrationForm.patchValue({ countryId: kazakhstan.id });
        }
      },
      error: (err) => {
        console.error('Ошибка загрузки стран:', err);
        this.error = this.languageService.translate('registration.freightForwarder.errors.default') || 'Ошибка при загрузке данных';
      }
    });
  }

  onSubmit(): void {
    if (this.registrationForm.invalid) {
      this.markFormGroupTouched(this.registrationForm);
      return;
    }

    this.loading = true;
    this.error = null;

    const formValue = this.registrationForm.value;
    const request = {
      email: formValue.email,
      fullName: formValue.fullName,
      phone: formValue.phone || undefined,
      password: formValue.password,
      companyName: formValue.companyName,
      countryId: formValue.countryId
    };

    this.registrationService.registerFreightForwarder(request).subscribe({
      next: () => {
        this.success = true;
        this.loading = false;
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      },
      error: (err) => {
        this.loading = false;
        if (err.error && err.error.message) {
          this.error = err.error.message;
        } else if (err.error && typeof err.error === 'string') {
          this.error = err.error;
        } else {
          this.error = this.languageService.translate('registration.freightForwarder.errors.default') || 'Ошибка при регистрации. Попробуйте еще раз.';
        }
      }
    });
  }

  markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.registrationForm.get(fieldName);
    if (field && field.touched && field.errors) {
      if (field.errors['required']) {
        return this.languageService.translate('registration.freightForwarder.errors.required') || 'Это поле обязательно для заполнения';
      }
      if (field.errors['email']) {
        return this.languageService.translate('registration.freightForwarder.errors.email') || 'Введите корректный email адрес';
      }
      if (field.errors['minlength']) {
        return this.languageService.translate('registration.freightForwarder.errors.minlength', {
          min: field.errors['minlength'].requiredLength
        }) || `Минимальная длина: ${field.errors['minlength'].requiredLength} символов`;
      }
      if (field.errors['maxlength']) {
        return this.languageService.translate('registration.freightForwarder.errors.maxlength', {
          max: field.errors['maxlength'].requiredLength
        }) || `Максимальная длина: ${field.errors['maxlength'].requiredLength} символов`;
      }
      if (field.errors['passwordMismatch']) {
        return this.languageService.translate('registration.freightForwarder.errors.passwordMismatch') || 'Пароли не совпадают';
      }
    }
    return '';
  }
}
